import { AlertTriangle, ArrowRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../lib/utils';
import { currencyFormatter } from '../../services/helperService';
import type { QuantityChangePreview, QuantityInfoValue } from '../../services/orderhistory/quantityChangeTypes';
import { borderPrimary, primaryText, secondaryText } from '../../styling/stylingHelper';

const amountText = (value: number): string => String(Number(value.toFixed(4)));

function BeforeAfter({ before, after }: { before: string; after: string }) {
  return <span className={cn('inline-flex flex-wrap items-center gap-1.5 text-sm tabular-nums', primaryText)}>
    <span className={secondaryText}>{before}</span>
    <ArrowRight className={cn('h-3.5 w-3.5 shrink-0', secondaryText)} aria-hidden="true" />
    <span className="font-medium">{after}</span>
  </span>;
}

function Group({ title, help, children }: { title: string; help?: string; children: React.ReactNode }) {
  return <section className="space-y-2">
    <div>
      <h3 className={cn('text-sm font-semibold', primaryText)}>{title}</h3>
      {help && <p className={cn('text-xs', secondaryText)}>{help}</p>}
    </div>
    <div className={cn('divide-y divide-borderPrimary rounded-lg border dark:divide-borderPrimary-dark', borderPrimary)}>{children}</div>
  </section>;
}

function Row({ label, sub, children }: { label: string; sub?: string; children: React.ReactNode }) {
  return <div className="flex flex-col gap-1 px-3 py-2 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
    <div className="min-w-0">
      <p className={cn('break-words text-sm', primaryText)}>{label}</p>
      {sub && <p className={cn('text-xs', secondaryText)}>{sub}</p>}
    </div>
    <div className="shrink-0">{children}</div>
  </div>;
}

function Notice({ children }: { children: React.ReactNode }) {
  return <p role="note" className="flex items-start gap-2 rounded-lg bg-amber-50 p-3 text-sm text-amber-800 dark:bg-amber-900/20 dark:text-amber-200">
    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
    <span>{children}</span>
  </p>;
}

/** What a quantity change will do, before it happens. Shared by the order page editor and the AI Checker override. */
export function QuantityChangeReview({ preview }: { preview: QuantityChangePreview }) {
  const { t } = useTranslation();
  const label = (key: string, vars?: Record<string, unknown>) => t(`orderHistory.quantityChange.${key}`, vars ?? {});
  const unit = (value: unknown) => (value === 'p' ? label('pieces') : String(value ?? ''));
  const quantity = (info: QuantityInfoValue | null) => {
    if (!info) return label('unknown');
    const amount = `${amountText(info.stdAmount)} ${unit(info.stdUnit)}`;
    return info.stdMultiplier > 1 ? `${amountText(info.stdMultiplier)} × ${amount}` : amount;
  };
  const countUnits = (units: Array<{ unit: string; factor: number; stdUnit: unknown }>) =>
    units.map(item => `1 ${item.unit} = ${amountText(item.factor)} ${unit(item.stdUnit)}`).join(', ');
  const nothingElse = !preview.recipes.length && !preview.sections.length && !preview.pastCounts && !preview.minimum && !preview.priceOverride
    && !preview.toCheck.length && !preview.notConverted;
  const originalUnknown = preview.reset && !preview.to;

  return <div className="space-y-5">
    <Group title={label(preview.reset ? 'resetTitle' : 'quantityTitle')}>
      <Row label={preview.productName}>
        <BeforeAfter before={quantity(preview.from)} after={originalUnknown ? label('supplierQuantity') : quantity(preview.to)} />
      </Row>
    </Group>

    {preview.notConverted && <Notice>{label('notConverted')}</Notice>}
    {originalUnknown && <Notice>{label('originalUnknown')}</Notice>}
    {preview.assumesWholePacks && <Notice>{label('assumesWholePacks')}</Notice>}
    {nothingElse && !originalUnknown && <p className={cn('text-sm', secondaryText)}>{label('nothingElse')}</p>}

    {preview.recipes.length > 0 && <Group title={label('recipesTitle', { count: preview.recipes.length })} help={label('recipesHelp')}>
      {preview.recipes.map((item, index) => <Row key={`${item.name}-${index}`} label={item.name} sub={item.dishAmount ? label('dishAmount') : undefined}>
        <BeforeAfter before={`${amountText(item.from)} ${unit(item.fromUnit)}`} after={`${amountText(item.to)} ${unit(item.toUnit)}`} />
      </Row>)}
    </Group>}

    {preview.sections.length > 0 && <Group title={label('sectionsTitle')} help={label('sectionsHelp')}>
      {preview.sections.map(section => <Row key={section.label} label={section.label}>
        <BeforeAfter before={countUnits(section.from)} after={countUnits(section.to)} />
      </Row>)}
    </Group>}

    {preview.pastCounts && <Group title={label('pastCountsTitle')} help={label('pastCountsHelp')}>
      <Row label={label('pastCountsValue')} sub={label('pastCountsSize', { lines: preview.pastCounts.lines, counts: preview.pastCounts.counts })}>
        <BeforeAfter before={currencyFormatter.format(preview.pastCounts.valueBefore)} after={currencyFormatter.format(preview.pastCounts.valueAfter)} />
      </Row>
    </Group>}

    {(preview.minimum || preview.priceOverride) && <Group title={label('settingsTitle')}>
      {preview.minimum && <Row label={label('minimum')} sub={`${amountText(preview.minimum.count)} ${preview.minimum.countUnit}`}>
        <BeforeAfter before={amountText(preview.minimum.fromStd)} after={`${amountText(preview.minimum.toStd)} ${unit(preview.minimum.toUnit)}`} />
      </Row>}
      {preview.priceOverride && <Row label={label('manualPrice')}>
        <BeforeAfter before={`€${preview.priceOverride.from.toFixed(4)} / ${unit(preview.priceOverride.fromUnit)}`} after={`€${preview.priceOverride.to.toFixed(4)} / ${unit(preview.priceOverride.toUnit)}`} />
      </Row>}
    </Group>}

    {preview.renameLabels.map(item => <Notice key={`${item.unit}-${item.label}`}>{label('renameLabel', { unit: item.unit, label: item.label, newUnit: unit(preview.to?.stdUnit) })}</Notice>)}

    {preview.toCheck.length > 0 && <Group title={label('toCheckTitle')} help={label('toCheckHelp')}>
      {preview.toCheck.map(item => <Row key={`${item.kind}-${item.name}`} label={item.name} sub={label(`toCheck_${item.kind}`)}>{null}</Row>)}
    </Group>}
  </div>;
}
